import { AI_CREDITS } from '../pricing';
import contentFiles from './manifest.json';
import type { HelpGuide, HelpTopic } from './types';
import guides_how_it_works from './guides/how-it-works.json';
import guides_supported_phones from './guides/supported-phones.json';
import guides_employee_privacy from './guides/employee-privacy.json';
import guides_employee_setup from './guides/employee-setup.json';
import guides_employee_controls from './guides/employee-controls.json';
import guides_recording_basics from './guides/recording-basics.json';
import guides_calls_contacts from './guides/calls-contacts.json';
import guides_plan_ai from './guides/plan-ai.json';
import troubleshooting_calls from './troubleshooting/calls.json';
import troubleshooting_recordings from './troubleshooting/recordings.json';
import troubleshooting_offline from './troubleshooting/offline.json';
import troubleshooting_connect from './troubleshooting/connect.json';
import troubleshooting_ai from './troubleshooting/ai.json';
import troubleshooting_webhooks from './troubleshooting/webhooks.json';
import deviceSetupGuideDetails from './details/phone-setup.json';

export { contentFiles };
export const guideCategories = [
  {
    id: 'start',
    title: 'Start here',
    description: 'The basics, supported phones and employee privacy.',
  },
  { id: 'team', title: 'Team & setup', description: 'Connect your team and choose what syncs.' },
  {
    id: 'using',
    title: 'Using CallSync',
    description: 'Calls, recordings and your workspace plan.',
  },
];
export const guideCategoryTitle = (id: string): string =>
  guideCategories.find((category) => category.id === id)?.title ?? id;
export type { GuideSection, HelpGuide, HelpStep, HelpTopic } from './types';
export const HELP_GUIDES: HelpGuide[] = /* @__PURE__ */ [
  guides_how_it_works,
  guides_supported_phones,
  guides_employee_privacy,
  guides_employee_setup,
  guides_employee_controls,
  guides_recording_basics,
  guides_calls_contacts,
  guides_plan_ai,
].map(resolveContentFacts);
export const HELP_TOPICS: HelpTopic[] = /* @__PURE__ */ [
  troubleshooting_calls,
  troubleshooting_recordings,
  troubleshooting_offline,
  troubleshooting_connect,
  troubleshooting_ai,
  troubleshooting_webhooks,
].map(resolveContentFacts);
export const contentDetails = {
  deviceSetupGuide: deviceSetupGuideDetails,
};

/** Render named values as plain text; React handles escaping at the UI boundary. */
export function formatContent(template: string, values: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (token, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token,
  );
}

export function guideSearchText(guide: HelpGuide): string {
  return [
    guide.title,
    guide.description,
    guide.keywords,
    ...guide.sections.flatMap((section) => [
      section.title,
      ...(section.paragraphs ?? []),
      ...(section.bullets ?? []),
      ...(section.table?.rows.flat() ?? []),
    ]),
  ]
    .join(' ')
    .toLowerCase();
}

/** Values shared with billing; guide copy must not duplicate entitlement numbers. */
export const contentFacts = {
  secondsPerCredit: AI_CREDITS.secondsPerCredit,
  fourMinuteCallCredits: Math.ceil((4 * 60) / AI_CREDITS.secondsPerCredit),
};
function resolveContentFacts<T>(value: T): T {
  if (typeof value === 'string')
    return formatContent(value, {
      secondsPerCredit: AI_CREDITS.secondsPerCredit,
      fourMinuteCallCredits: Math.ceil((4 * 60) / AI_CREDITS.secondsPerCredit),
    }) as T;
  if (Array.isArray(value)) return value.map(resolveContentFacts) as T;
  if (value !== null && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, resolveContentFacts(item)]),
    ) as T;
  return value;
}
